import { defineStore } from 'pinia';
import systemsApi from '@/app/features/ams/systems/systems-service';
import {
  PaginationParams,
  SearchParams,
} from '@/app/shared/types/params/request-params.interface';
import { SystemState } from '@/app/shared/types/stores/systems-state.interface';
import { DetailPage } from '@/app/shared/types/models/detail.page.interface';
import { TabHeader } from '@/app/shared/types/tab-header.interface';
import { PaginatedResponse } from '@/app/shared/types/api/api-response.interface';
import { System } from '@/app/shared/types/system.interface';
import { Pageable } from '@/app/shared/types/api/pageable.interface';
import { SystemInfo } from '@/app/shared/types/systeminfo.interface';

export const useSystemsStore = defineStore({
  id: 'systemStoreId',
  state: () =>
    ({
      clientSystems: {
        content: [],
        pageable: {} as Pageable,
        last: false,
        totalPages: 1,
        totalElements: 0,
        number: 0,
        size: 50,
        first: true,
        numberOfElements: 0,
        empty: true,
      },
      systems: {} as PaginatedResponse<System>,
      systemDetails: new Map(),
      tabs: [{ id: 0, name: 'All Systems' }],
      selectedTab: 0,
      refreshGridData: false,
      systemAttributes: [],
      requestParams: { page: 0, size: 10 },
    } as SystemState),
  actions: {
    async fetchAllSystemsByClient(clientId: number) {
      const response = await systemsApi.getSystems(clientId, {
        page: 0,
        size: 100,
      });
      this.clientSystems = response.data.data;
    },
    async fetchSystemMetaData(clientId: number, params: PaginationParams) {
      const {
        data: { data },
      } = await systemsApi.getSystems(clientId, params);
      this.systems = data;
    },
    async fetchSystemAttributes(clientId: number, systemId: number) {
      const {
        data: { data },
      } = await systemsApi.getSystemAttributesList(clientId, systemId);
      this.systemAttributes = data;
    },
    removeTabFromSystemNamesList(systemId: number | string) {
      const fIndex = this.tabs.findIndex((tab: any) => tab.id === systemId);
      fIndex > 0 && this.tabs.splice(fIndex, 1);
    },
    async createSystemTab(clientId: number, detailPage: DetailPage) {
      const { id, name, isNew } = detailPage;
      if (!isNew) {
        const {
          data: { data },
        } = await systemsApi.getSystemById(clientId, id);
        detailPage.systemData = data;
        this.systemDetails.set(id, detailPage);
      }
      //if tab is already opened, switch to that tab
      const tabIndex = this.tabs.findIndex(
        (tab: TabHeader) => tab.name === name
      );

      if (tabIndex === -1) {
        this.tabs.push({
          id,
          name,
          autoLocked: false,
        });
        this.selectedTab = this.tabs.length - 1;
      } else {
        this.selectedTab = tabIndex;
      }
    },
    async createNewSystemTab(detailPage: DetailPage, defaultData: any) {
      const { id, name } = detailPage;
      if (detailPage.isNew) {
        detailPage.systemData = defaultData;
        this.systemDetails.set(detailPage.id, detailPage);
      }
      //if tab is already opened, switch to that tab
      const tabIndex = this.tabs.findIndex(
        (tab: TabHeader) => tab.name === detailPage.name
      );

      if (tabIndex === -1) {
        this.tabs.push({ id, name });
        this.selectedTab = this.tabs.length - 1;
      } else {
        this.selectedTab = tabIndex;
      }
    },
    setTabDirtyFieldById(systemId: string | number, value: boolean) {
      const data = this.systemDetails.get(systemId);
      data.dirty = value;
    },
    setTabNewFieldById(systemId: string | number, value: boolean) {
      const data = this.systemDetails.get(systemId);
      data.isNew = value;
    },
    setTabIsCopyStatusToDefaultById(attributeId: string | number) {
      const data = this.systemDetails.get(attributeId);
      data.isCopied.id = -1;
      data.isCopied.isCopy = false;
    },
    saveCreatedSystem(oldId: number, data: SystemInfo) {
      const { id, name } = data;
      this.systemDetails.delete(oldId);
      this.systemDetails.set(id, {
        id,
        name,
        isNew: false,
        dirty: false,
        isCopied: {
          id: -1,
          isCopy: false,
        },
        systemData: data,
      });
      const fIndex = this.tabs.findIndex((elem) => elem.id === oldId);

      if (fIndex !== -1) {
        this.tabs[fIndex].id = data.id;
      }
      this.tabs[fIndex].id = fIndex > 0 ? id : -1;
      this.tabs[fIndex].name = fIndex > 0 ? name : '';
    },
    deleteAttribute(systemId: number) {
      this.systemDetails.delete(systemId);
      this.removeTabFromSystemNamesList(systemId);
    },
    updateTabNameFieldById(systemId: number | string, value: string) {
      const fIndex = this.tabs.findIndex((elem) => elem.id === systemId);
      this.tabs[fIndex].name = value;
    },
    setRefreshGridData(value: boolean) {
      this.refreshGridData = value;
    },
    updateRequestParams(params: SearchParams) {
      Object.keys(params).forEach((key) => {
        this.requestParams[key as keyof SearchParams] =
          params[key as keyof SearchParams];
      });
    },
    resetRequestParams() {
      this.requestParams = {};
    },
    async deleteSystem(clientId: number, id: number) {
      await systemsApi.deleteSystemById(clientId, id);
    },
  },
});
